import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, input, model, output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DatepickerComponent } from 'src/app/@core/components/ui/datepicker/datepicker.component';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';
import { CONTRACT_USER_ROLE, IDocumentVersion, IProject, IStatus, IUser } from 'src/app/@core/models/model';
import { USER_ROLE } from 'src/app/@core/models/enums';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { BreadcrumbComponent } from 'src/app/@shared/components/breadcrumb/breadcrumb.component';
import { NotificationsComponent } from 'src/app/@shared/components/notifications/notifications.component';
import { ScrollResetDirective } from 'src/app/@shared/directives/scroll-reset.directive';
import { CONTRACT_STATUS } from 'src/app/@shared/components/badge/badge.enums';
import { SVG, TOP_BAR_SVG } from 'src/app/common/svgs';
import { IContract } from 'src/app/contract-management/models/contract-management.model';
import { ShareDocComponent } from '../share-doc/share-doc.component';
import { environment } from 'src/environments/environment.dev';

export enum ContractTopBarView {
  HOME = 'Home',
  VERSION = 'Version',
  COMPARE = 'Compare',
}

@Component({
  selector: 'app-contract-top-bar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TooltipModule,
    ButtonComponent,
    DatepickerComponent,
    IconComponent,
    MenuComponent,
    MenuModule,
    PopoverComponent,
    NotificationsComponent,
    ScrollResetDirective,
    ShareDocComponent,
    BreadcrumbComponent,
    CreditsOverviewComponent,
  ],
  templateUrl: './contract-top-bar.component.html',
})
export class ContractTopBarComponent {

  //#region Constants & Enums
  SVG = SVG;
  TOP_BAR_SVG = TOP_BAR_SVG;
  CONTRACT_STATUS = CONTRACT_STATUS;
  CONTRACT_USER_ROLE = CONTRACT_USER_ROLE;
  ContractTopBarView = ContractTopBarView;
  ENV = environment;
  //#endregion

  //#region Services (Compare variant)
  subscriptionState = inject(SubscriptionStateService);
  //#endregion

  @ViewChild('dueDatePicker') dueDatePickerViewRef?: DatepickerComponent;
  @ViewChild('overflowMenuBtn', { read: ElementRef }) overflowMenuBtnRef?: ElementRef;

  overflowMenuItems = computed<CustomMenuItem[]>(() => {
    const items: CustomMenuItem[] = [];
    if (this.project()) {
      items.push({ label: 'Project', icon: SVG.PROJECT_FOLDER, iconSource: IconSourceEnum.SVG, callback: () => this.openProject() });
    }
    if (this.fileUrl() && this.canDownload()) {
      items.push({ label: 'Download', icon: SVG.DOWNLOAD, iconSource: IconSourceEnum.SVG, callback: () => this.downloadContractClicked.emit() });
    }
    if ((this.currentRole() === CONTRACT_USER_ROLE.OWNER || this.isEditor()) && this.isSameOrganization() && !this.isAnonymousUser()) {
      items.push({ label: 'Due Date', icon: SVG.CALENDER, iconSource: IconSourceEnum.SVG, callback: () => this.openDueDatePicker() });
    }
    return items;
  });

  openDueDatePicker(): void {
    const el = this.overflowMenuBtnRef?.nativeElement;
    if (!this.dueDatePickerViewRef || !el) return;
    const rect = el.getBoundingClientRect();
    const syntheticEvent = { target: el, clientX: rect.left, clientY: rect.bottom } as unknown as MouseEvent;
    this.dueDatePickerViewRef.datepickerComponent.toggle(syntheticEvent);
  }

  isEditingTitle = false;

  enableScroll() {
    this.isEditingTitle = true;
  }
  
  handleBlur(event: any) {
    this.isEditingTitle = false;
    this.contractNameUpdated.emit(event);
  }
  
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  }


  //#region View
  view = input(ContractTopBarView.HOME);
  //#endregion

  //#region Shared Inputs – End Icons
  supportMenuItems = input<CustomMenuItem[]>([]);
  infoMenuItems = input<CustomMenuItem[]>([]);
  isRecording = input(false);
  items = input<MenuItem[]>([]);
  //#endregion

  //#region Home View – Left Section
  isAnonymousUser = input(false);
  contract = input<(IContract & { containsForm: boolean }) | null>(null);
  isOwner = input(false);
  isSameOrganization = input(false);
  permissions = input<{ canEdit: boolean; canView: boolean; canComment: boolean; restricted: boolean }>({
    canEdit: false, canView: false, canComment: false, restricted: true,
  });
  externalDocument = input(false);
  currentUser = input<IUser | null | undefined>(null);
  projectId = input('');
  //#endregion

  //#region Home View – Status
  statusMenuItems = input<(MenuItem & { active: boolean })[]>([]);
  selectedStatus = input<IStatus | null>(null);
  loadingStatuses = input(false);
  canEditStatus = input(false);
  //#endregion

  //#region Home View – Document Controls
  project = input<IProject | null>(null);
  loading = input(false);
  fileUrl = input<string | null>(null);
  docUpdatedAt = input('');
  currentRole = input('');
  isEditor = input(false);
  canDownload = input(false);
  //#endregion

  //#region Home View – Due Date
  selectedDueDate = model<Date | null>(null);
  currentDate = input<Date>(new Date());
  projectDueDate = input<Date | null>(null);
  //#endregion

  //#region Home View – Review & Action
  isCurrentUserReviewedDoc = input(false);
  showReviewBtn = input(false);
  labelText = input('');
  actionButton = input(false);
  //#endregion

  //#region Versions View
  selectedVersion = input<IDocumentVersion | null>(null);
  currentVersion = input<IDocumentVersion | null>(null);
  //#endregion

  //#region Outputs
  goBackClicked = output<void>();
  contractNameUpdated = output<Event>();
  statusItemClicked = output<{ item: any; menu: PopoverComponent }>();
  lastModifiedRequested = output<void>();
  createDialogOpened = output<void>();
  downloadContractClicked = output<void>();
  versionsHistoryToggled = output<void>();
  dueDateSelected = output<any>();
  dueDateCleared = output<void>();
  reviewDocumentClicked = output<void>();
  actionTriggered = output<void>();
  menuItemClicked = output<MenuItem>();
  //#endregion

  canAccessPricing(): boolean {
    return this.currentUser()?.role === USER_ROLE.SUPER_ADMIN;
  }

  navigateToPricing(): void {
    this.router.navigate(['/pricing']);
  }
  //#endregion

  //#region Internal Methods
  private router = inject(Router);

  openProject(): void {
    const id = this.projectId();
    if (id) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/projects/dashboard'], { queryParams: { projectId: id } })
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  blurSpan(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  checkDueDatePassed(date: Date | string | null): boolean {
    if (!date) return false;
    return new Date(date) < new Date();
  }

  isSelectedPastDate(date: any): boolean {
    const selected = this.selectedDueDate();
    if (!selected) return false;
    const s = new Date(selected);
    s.setHours(0, 0, 0, 0);
    const check = new Date(date.year, date.month, date.day);
    check.setHours(0, 0, 0, 0);
    return check.getTime() === s.getTime() && check < this.currentDate();
  }

  get mobileMenuItems(): MenuItem[] {
    return this.items().map(item => ({
      ...item,
      command: () => this.menuItemClicked.emit(item),
    }));
  }

  handleMenuItemClick(item: MenuItem): void {
    this.menuItemClicked.emit(item);
  }

  goBack(): void {
    // If we are coming from copied compare url path , then navigate to explore page instead last opened page
    const previousUrl = this.router.lastSuccessfulNavigation?.previousNavigation?.extractedUrl?.toString();
    if (previousUrl?.includes('/compare')) {
      this.router.navigate(['/documents']);
    } else {
      history.back();
    }
  }
  //#endregion
}
